import { DownloadLink } from "./DownloadLink";
import { ArrowRight, CalendarCheck, ChevronDown, Menu, Sparkles, X } from "lucide-react";
import { RegionSwitcher } from "./RegionSwitcher";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lang, t } from "../i18n";
import { unitedStatesHomePath } from "../homeContent";
import { industrySolutionPath } from "../industrySolutions";
import { demoPath, downloadPath, localeRoot, pricingHref, sectionHref } from "../routes";
import { reportLeadEntryConversion } from "../tracking";
import { BrandLogo } from "./BrandLogo";

const solutionMenuCopy = {
  en: {
    overviewName: "Overview",
    overviewDescription: "AI reception and workflow automation for your business.",
    name: "Dental Clinics",
    description: "AI reception, patient conversations, and appointment booking for your practice.",
  },
  zh: {
    overviewName: "通用解决方案",
    overviewDescription: "面向不同业务的 AI 智能接待与工作流自动化。",
    name: "牙科诊所",
    description: "智能接待患者、管理多渠道对话，帮助诊所完成预约。",
  },
};

function SolutionMenuCards({ lang, search, onNavigate }: { lang: Lang; search: string; onNavigate: () => void }) {
  const text = solutionMenuCopy[lang];
  const items = [
    { href: localeRoot(lang, search), name: text.overviewName, description: text.overviewDescription, Icon: Sparkles },
    { href: `${industrySolutionPath("dental-clinics")}${search}`, name: text.name, description: text.description, Icon: CalendarCheck },
  ];
  return items.map(({ href, name, description, Icon }) => (
    <Link
      key={href}
      to={href}
      onClick={onNavigate}
      className="group flex items-start gap-3 rounded-[16px] p-4 text-[#252030] no-underline transition-colors hover:bg-[#f7f2fc] focus-visible:bg-[#f7f2fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#6d35cf]/50 sm:gap-4 sm:p-5"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-[#f1e8fb] text-[#6d35cf]">
        <Icon className="h-6 w-6" aria-hidden="true" strokeWidth={1.8} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-3 text-[15px] font-bold tracking-[-.02em]">
          {name}<ArrowRight className="h-4 w-4 shrink-0 text-[#6d35cf] transition-transform group-hover:translate-x-1 motion-reduce:transform-none" aria-hidden="true" />
        </span>
        <span className="mt-1.5 block text-[13px] leading-[1.65] text-[#756b7f]">{description}</span>
      </span>
    </Link>
  ));
}

// 渲染官网顶部导航，语言只由当前路由决定。
export function Nav({ lang, autoHideOnScroll = false, showRegionSelector = false }: { lang: Lang; autoHideOnScroll?: boolean; showRegionSelector?: boolean }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement>(null);
  const desktopSolutionTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [solutionOpen, setSolutionOpen] = useState(false);
  const [hiddenByScroll, setHiddenByScroll] = useState(false);
  const [compactByScroll, setCompactByScroll] = useState(false);
  const scrollAnchorRef = useRef(0);
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);
  const desktopSolutionPanelId = "desktop-solution-panel";
  const mobileNavigationPanelId = "mobile-navigation-panel";
  const mobileSolutionPanelId = "mobile-solution-panel";
  const primaryCtaHref = downloadPath(lang, "nav_download", undefined, search, pathname);
  const primaryCtaLabel = t(lang, "nav.cta");

  const navLinks = [
    { key: "nav.how", href: sectionHref(lang, "#how", search) },
    { key: "nav.pricing", href: pricingHref(lang, pathname, search) },
    { key: "nav.demo", href: demoPath(lang, search) },
  ];

  useEffect(() => setOverlayRoot(document.body), []);

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setSolutionOpen(false);
        setMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const focusTarget = menuOpen ? mobileMenuTriggerRef.current : solutionOpen ? desktopSolutionTriggerRef.current : null;
        setSolutionOpen(false);
        setMenuOpen(false);
        window.requestAnimationFrame(() => focusTarget?.focus());
      }
    };

    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen, solutionOpen]);

  useEffect(() => {
    if (!autoHideOnScroll) {
      setHiddenByScroll(false);
      setCompactByScroll(false);
      return;
    }

    scrollAnchorRef.current = window.scrollY;
    setCompactByScroll(window.scrollY >= 140);
    let frameId = 0;
    const updateVisibility = () => {
      frameId = 0;
      const currentY = window.scrollY;
      const delta = currentY - scrollAnchorRef.current;
      const navHasFocus = navRef.current?.contains(document.activeElement);
      setCompactByScroll(currentY >= 140);

      if (currentY < 140 || menuOpen || solutionOpen || navHasFocus) {
        setHiddenByScroll(false);
        scrollAnchorRef.current = currentY;
        return;
      }

      if (delta >= 48) {
        setHiddenByScroll(true);
        scrollAnchorRef.current = currentY;
      } else if (delta <= -56) {
        setHiddenByScroll(false);
        scrollAnchorRef.current = currentY;
      }
    };
    const onScroll = () => {
      if (!frameId) frameId = window.requestAnimationFrame(updateVisibility);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [autoHideOnScroll, menuOpen, solutionOpen]);

  const closeMenus = () => {
    setSolutionOpen(false);
    setMenuOpen(false);
  };

  return (
    <>
      {/* Keep the full-page backdrop outside the transformed, animated header. */}
      {overlayRoot && (solutionOpen || menuOpen) ? createPortal(
        <div data-site-menu-backdrop aria-hidden="true" onPointerDown={closeMenus} className="fixed inset-0 z-[45] cursor-default bg-[#160b23]/[.15] backdrop-blur-[3px]" />,
        overlayRoot,
      ) : null}
      <nav ref={navRef} className={`fixed left-0 right-0 top-0 z-50 transition duration-300 ease-out motion-reduce:transition-none ${compactByScroll ? "bg-white/90 px-5 pt-0 shadow-[0_8px_24px_rgba(21,20,24,.08)] backdrop-blur-xl sm:px-10" : "px-5 pt-7 sm:px-10"} ${hiddenByScroll ? "pointer-events-none -translate-y-[120px] opacity-0" : "translate-y-0 opacity-100"}`}>
        <div className={`relative z-10 mx-auto flex max-w-[1200px] items-center border border-black/5 bg-white px-5 transition-[height,border-radius,box-shadow] duration-300 ${compactByScroll ? "h-[64px] rounded-b-2xl border-t-0 shadow-none" : "h-[70px] rounded-2xl shadow-[0_4px_20px_rgba(21,20,24,.10)]"}`}>
          <Link to={localeRoot(lang, search)} onClick={closeMenus} className="flex flex-shrink-0 items-center rounded-md no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf]/60 focus-visible:ring-offset-2">
            <BrandLogo />
          </Link>

          <div className="flex flex-1 justify-center">
            <div className="hidden items-center gap-6 lg:flex">
              <div className="relative">
                <button
                  ref={desktopSolutionTriggerRef}
                  id="desktop-solution-trigger"
                  type="button"
                  aria-expanded={solutionOpen}
                  aria-controls={desktopSolutionPanelId}
                  onClick={() => setSolutionOpen((open) => !open)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] border px-3 py-2 text-sm font-medium transition duration-200 focus-visible:outline-none motion-reduce:transition-none ${solutionOpen ? "border-[#6d35cf] bg-[#f6f1fb] text-[#5b24c9]" : "border-transparent text-[#3a383f] hover:bg-[#f8f4fa] hover:text-[#100f12] focus-visible:bg-[#f6f1fb] focus-visible:text-[#5b24c9]"}`}
                >
                  {t(lang, "nav.solution")}
                  <ChevronDown className={`h-4 w-4 transition-transform ${solutionOpen ? "rotate-180" : ""}`} />
                </button>

                <div
                  id={desktopSolutionPanelId}
                  aria-labelledby="desktop-solution-trigger"
                  aria-hidden={!solutionOpen}
                  className={`absolute left-1/2 top-[calc(100%+22px)] w-[380px] -translate-x-1/2 rounded-[20px] border border-[#eee7f4] bg-white p-1.5 shadow-[0_24px_64px_-18px_rgba(37,17,62,.28)] transition duration-200 motion-reduce:transition-none ${solutionOpen ? "visible translate-y-0 opacity-100" : "invisible pointer-events-none -translate-y-2 opacity-0"}`}
                >
                  <SolutionMenuCards lang={lang} search={search} onNavigate={closeMenus} />
                </div>
              </div>

              {navLinks.map((link) => (
                <Link key={link.key} to={link.href} onClick={closeMenus} className="whitespace-nowrap rounded-md text-sm font-medium text-[#3a383f] no-underline transition-colors hover:text-[#100f12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf]/55 focus-visible:ring-offset-4">
                  {t(lang, link.key)}
                </Link>
              ))}
            </div>

            <button
              ref={mobileMenuTriggerRef}
              type="button"
              aria-label={menuOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={menuOpen}
              aria-controls={mobileNavigationPanelId}
              onClick={() => {
                setMenuOpen((open) => !open);
                setSolutionOpen(false);
              }}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-[#eaddec] bg-[#fbf8fc] px-3 text-sm font-extrabold text-[#3a383f] transition-colors hover:bg-[#f4eef8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf]/55 focus-visible:ring-offset-2 lg:hidden"
            >
              {menuOpen ? <X className="h-4 w-4 text-[#5b24c9]" strokeWidth={2.6} /> : <Menu className="h-4 w-4 text-[#5b24c9]" strokeWidth={2.6} />}
              <span className="hidden sm:inline">{lang === "zh" ? "菜单" : "Menu"}</span>
            </button>
          </div>

          <div className="flex flex-shrink-0 items-center gap-3 sm:gap-4">
            {showRegionSelector ? (
              <RegionSwitcher
                region="us"
                label="Region"
                labels={{ us: "United States", hk: "Hong Kong" }}
                onChange={(nextRegion) => navigate(nextRegion === "hk" ? `/hk/ai-receptionist${search}` : `${unitedStatesHomePath}${search}`)}
                withDivider
              />
            ) : null}
            <DownloadLink
              to={primaryCtaHref}
              onClick={() => {
                reportLeadEntryConversion(search);
                closeMenus();
              }}
              className="whitespace-nowrap rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white no-underline transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf]/55 focus-visible:ring-offset-2 sm:px-5"
              style={{ background: "linear-gradient(135deg, #5223b1 0%, #7823b1 50%, #e930c9 100%)" }}
            >
              {primaryCtaLabel}
            </DownloadLink>
          </div>

          {menuOpen ? (
            <div id={mobileNavigationPanelId} className="absolute left-0 right-0 top-[calc(100%+10px)] max-h-[calc(100dvh-120px)] overflow-y-auto rounded-[20px] bg-white p-2 shadow-[0_24px_64px_rgba(36,16,59,.20),inset_0_1px_0_rgba(255,255,255,.28)] backdrop-blur-[24px] backdrop-saturate-[130%] lg:hidden">
              <button
                type="button"
                aria-expanded={solutionOpen}
                aria-controls={mobileSolutionPanelId}
                onClick={() => setSolutionOpen((open) => !open)}
                className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-extrabold text-[#3a383f] hover:bg-white/45 focus-visible:bg-white/55 focus-visible:outline-none"
              >
                {t(lang, "nav.solution")}
                <ChevronDown className={`h-4 w-4 transition-transform ${solutionOpen ? "rotate-180" : ""}`} />
              </button>
              <div id={mobileSolutionPanelId} className={solutionOpen ? "mb-2 rounded-[16px] bg-white" : "hidden"}>
                <SolutionMenuCards lang={lang} search={search} onNavigate={closeMenus} />
              </div>
              <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
                {navLinks.map((link) => <Link key={link.key} to={link.href} onClick={closeMenus} className="rounded-xl px-3 py-3 text-center text-sm font-extrabold text-[#3a383f] no-underline transition-colors hover:bg-white/45 hover:text-[#100f12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#6d35cf]/35">{t(lang, link.key)}</Link>)}
              </div>
            </div>
          ) : null}
        </div>
      </nav>
    </>
  );
}
